import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import AdminWrapper from "../../../components/AdminWrapper";
import { GetTenantFunnel } from "../../../redux/reducer/TenantFunnel";
import { FollowUpDialog, FollowUpFilterBar, FollowUpStatus } from "../../../components/followUp/FollowUpUI";
import {
  matchesFilter,
  matchesSearch,
  needsCall,
  rowSx,
  sortByUrgency,
  useFollowUpOverrides,
} from "../../../components/followUp/followUpUtils";

// Every tenant and every owner, wherever they've stalled between sign-up and
// a running tenancy — one screen for the team to trace exactly where someone
// is stuck and follow up. The OTS-fee step has its own detailed review
// screen (OtsFeeReceipts); this one links there instead of duplicating it.
const TABS = [
  {
    group: "Tenant side",
    key: "registered_only",
    label: "Registered Only",
    hint: "Signed up, but never sent a request to any property.",
    shape: "registered",
  },
  {
    group: "Tenant side",
    key: "awaiting_payment",
    label: "Accepted, Not Paid",
    hint: "The owner accepted their request, but they haven't paid — no cash slip, no online payment.",
    shape: "request",
  },
  {
    group: "Tenant side",
    key: "paid_cash",
    label: "Paid — Cash",
    hint: "Paid the owner in cash. The OTS service fee may still need following up — see OTS Fee Receipts.",
    shape: "request",
  },
  {
    group: "Tenant side",
    key: "paid_online",
    label: "Paid — Online",
    hint: "Paid through SafePay, Alfa or bank transfer.",
    shape: "request",
  },
  {
    group: "Owner side",
    key: "owner_pending_requests",
    label: "Requests Not Reviewed",
    hint: "A tenant is waiting — the owner has not accepted or rejected their request yet.",
    shape: "owner",
  },
  {
    group: "Owner side",
    key: "owner_payment_unverified",
    label: "Payment Not Confirmed",
    hint: "A tenant sent a cash payment receipt; the owner has not confirmed receiving it.",
    shape: "owner",
  },
  {
    group: "Owner side",
    key: "move_in_overdue",
    label: "Move-in Overdue",
    hint: "The tenant's chosen move-in date has passed, but the move-in was never confirmed.",
    shape: "owner",
  },
  {
    group: "Move-outs",
    key: "move_out_requests",
    label: "Move-out Requests",
    hint: "Either side has asked to end a tenancy and the other has not yet responded.",
    shape: "moveout",
  },
];

const money = (n) => `Rs ${Number(n || 0).toLocaleString()}`;
const days = (n) => (n == null ? "—" : `${n} day${n === 1 ? "" : "s"}`);
const when = (d) => (d ? moment(d).format("DD MMM YYYY") : "—");

const PAYMENT_STATUS_CHIP = {
  unpaid: { bg: "#fee2e2", color: "#991b1b" },
  failed: { bg: "#fee2e2", color: "#991b1b" },
  verifying: { bg: "#fef3c7", color: "#92400e" },
  verified: { bg: "#dcfce7", color: "#166534" },
  pending: { bg: "#fef3c7", color: "#92400e" },
};

const METHOD_LABEL = { safepay: "SafePay", alfa: "Alfa", bank: "Bank Transfer", cash: "Cash" };

// What the detail dialog shows for a row, and who can be called from it.
const detailsFor = (row, tab) => {
  if (tab === "registered_only") {
    return [
      { label: "Email", value: row.tenantEmail },
      { label: "Phone", value: row.tenantPhone },
      { label: "Profile completed?", value: row.hasProfile ? "Yes" : "No" },
      { label: "Registered", value: `${days(row.daysWaiting)} ago` },
      { label: "Account", value: row.isActive ? "Active" : "Inactive" },
    ];
  }
  if (tab === "move_out_requests") {
    return [
      { label: "Property", value: [row.flatName, row.flatCity].filter(Boolean).join(", ") },
      { label: "Requested by", value: String(row.requestedBy || "").toLowerCase() },
      { label: "Effective date", value: when(row.effectiveDate) },
      { label: "Waiting", value: days(row.daysWaiting) },
      { label: "Reason", value: row.reason },
    ];
  }
  return [
    { label: "Property", value: [row.flatName, row.flatCity].filter(Boolean).join(", ") },
    { label: "Amount", value: row.paymentAmount != null ? money(row.paymentAmount) : "" },
    { label: "Payment status", value: row.paymentStatus },
    { label: "Payment method", value: METHOD_LABEL[row.paymentMethod] || row.paymentMethod },
    ...(row.otsFee?.required
      ? [{ label: "OTS fee", value: `${money(row.otsFee.amount)} — ${row.otsFee.status}` }]
      : []),
    { label: "Move-in date", value: row.moveInDate },
    { label: "Move-in status", value: row.moveInStatus },
    { label: "Waiting", value: days(row.daysWaiting) },
  ];
};

const contactsFor = (row) => {
  const tenant = {
    party: "tenant",
    name: row.tenantName,
    phone: row.tenantPhone,
    email: row.tenantEmail,
  };
  const owner = {
    party: "owner",
    name: row.landlordName,
    phone: row.landlordPhone,
    email: row.landlordEmail,
  };
  return [tenant, owner].filter((c) => row.followUp?.parties?.[c.party]);
};

const TenantFunnel = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const open = useSelector((state) => state.sidebar.open);
  const { loading, data, counts, allowedStages } = useSelector((state) => state.tenantFunnel);

  // Fetched once — the server already returns every stage this admin is
  // allowed to see in one call, so switching tabs afterwards is instant and
  // needs no further requests.
  const [tab, setTab] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const { record, apply } = useFollowUpOverrides();

  useEffect(() => {
    dispatch(GetTenantFunnel({ stage: "all" }));
  }, [dispatch]);

  // Only the tabs the server actually sent data for — someone holding one of
  // the narrower permissions (instead of full VERIFY_PAYMENTS) sees just
  // their slice, not all eight, and never an empty tab they can't open.
  const visibleTabs = allowedStages
    ? TABS.filter((t) => allowedStages.includes(t.key))
    : [];

  useEffect(() => {
    if (!tab && visibleTabs.length > 0) {
      setTab(visibleTabs[0].key);
    }
  }, [tab, visibleTabs]);

  const tabRows = ((tab && data?.[tab]) || []).map(apply);
  const rows = sortByUrgency(
    tabRows.filter((r) => matchesFilter(r, filter) && matchesSearch(r, search))
  );
  const activeTab = TABS.find((t) => t.key === tab);

  const contactCell = (name, email, phone) => (
    <>
      <Typography variant="body2" fontWeight={600}>
        {name}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {phone || email || "—"}
      </Typography>
    </>
  );

  return (
    <>
      <AdminWrapper />
      <Box
        sx={{
          ml: { xs: 0, md: open ? "260px" : "65px" },
          transition: "margin 0.3s ease",
          minHeight: "100vh",
          bgcolor: "#f8fafc",
          p: { xs: 2, md: 3 },
          pt: { xs: 10, md: 11 },
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
          Follow-ups
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Every place a tenant or an owner can get stuck between sign-up and a running
          tenancy — trace where someone is, and who to contact next.
        </Typography>

        {tab && (
          <Paper sx={{ mb: 2, borderRadius: 2 }}>
            <Tabs
              value={tab}
              onChange={(e, value) => setTab(value)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                borderBottom: "1px solid #e5e7eb",
                "& .MuiTab-root": { textTransform: "none", fontWeight: 500 },
              }}
            >
              {visibleTabs.map((t) => (
                <Tab
                  key={t.key}
                  value={t.key}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      {`${t.label} (${counts?.[t.key] ?? 0})`}
                      {(data?.[t.key] || []).map(apply).filter(needsCall).length > 0 && (
                        <Chip
                          size="small"
                          label={`${(data?.[t.key] || []).map(apply).filter(needsCall).length} to call`}
                          sx={{ height: 20, bgcolor: "#fee2e2", color: "#991b1b", fontWeight: 700 }}
                        />
                      )}
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Paper>
        )}

        {activeTab && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {activeTab.hint}
          </Typography>
        )}

        {tab && (
          <FollowUpFilterBar
            rows={tabRows}
            search={search}
            onSearch={setSearch}
            filter={filter}
            onFilter={setFilter}
          />
        )}

        <Paper sx={{ borderRadius: 2, overflowX: "auto" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : !tab ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <Typography color="text.secondary">
                You don't have access to any follow-up view.
              </Typography>
            </Box>
          ) : rows.length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <Typography color="text.secondary">
                {tabRows.length > 0
                  ? "No rows match this filter."
                  : `Nothing in "${activeTab?.label}" right now.`}
              </Typography>
            </Box>
          ) : activeTab?.shape === "registered" ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tenant</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Profile completed?</TableCell>
                  <TableCell>Registered</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Follow-up</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.tenantId} hover sx={{ ...rowSx(row), cursor: "pointer" }} onClick={() => setSelected(row)}>
                    <TableCell>{contactCell(row.tenantName, row.tenantEmail)}</TableCell>
                    <TableCell>{row.tenantPhone || "—"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.hasProfile ? "Yes" : "No"}
                        sx={{
                          bgcolor: row.hasProfile ? "#dcfce7" : "#fee2e2",
                          color: row.hasProfile ? "#166534" : "#991b1b",
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>{days(row.daysWaiting)} ago</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.isActive ? "Active" : "Inactive"}
                        sx={{
                          bgcolor: row.isActive ? "#dcfce7" : "#e5e7eb",
                          color: row.isActive ? "#166534" : "#374151",
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <FollowUpStatus row={row} onOpen={() => setSelected(row)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : activeTab?.shape === "moveout" ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tenant</TableCell>
                  <TableCell>Owner</TableCell>
                  <TableCell>Property</TableCell>
                  <TableCell>Requested by</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Effective date</TableCell>
                  <TableCell>Waiting</TableCell>
                  <TableCell>Follow-up</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.requestId} hover sx={{ ...rowSx(row), cursor: "pointer" }} onClick={() => setSelected(row)}>
                    <TableCell>{contactCell(row.tenantName, row.tenantEmail, row.tenantPhone)}</TableCell>
                    <TableCell>{contactCell(row.landlordName, row.landlordEmail, row.landlordPhone)}</TableCell>
                    <TableCell>
                      {row.flatName}
                      {row.flatCity ? `, ${row.flatCity}` : ""}
                    </TableCell>
                    <TableCell sx={{ textTransform: "capitalize" }}>
                      {String(row.requestedBy || "").toLowerCase()}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>{row.reason || "—"}</TableCell>
                    <TableCell>{when(row.effectiveDate)}</TableCell>
                    <TableCell>{days(row.daysWaiting)}</TableCell>
                    <TableCell>
                      <FollowUpStatus row={row} onOpen={() => setSelected(row)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : activeTab?.shape === "owner" ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tenant</TableCell>
                  <TableCell>Owner</TableCell>
                  <TableCell>Property</TableCell>
                  {tab === "move_in_overdue" && <TableCell>Move-in date</TableCell>}
                  {tab === "owner_payment_unverified" && (
                    <TableCell align="right">Amount</TableCell>
                  )}
                  <TableCell>Waiting</TableCell>
                  <TableCell>Follow-up</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.requestId} hover sx={{ ...rowSx(row), cursor: "pointer" }} onClick={() => setSelected(row)}>
                    <TableCell>{contactCell(row.tenantName, row.tenantEmail, row.tenantPhone)}</TableCell>
                    <TableCell>{contactCell(row.landlordName, row.landlordEmail, row.landlordPhone)}</TableCell>
                    <TableCell>
                      {row.flatName}
                      {row.flatCity ? `, ${row.flatCity}` : ""}
                    </TableCell>
                    {tab === "move_in_overdue" && <TableCell>{row.moveInDate || "—"}</TableCell>}
                    {tab === "owner_payment_unverified" && (
                      <TableCell align="right">
                        <strong>{money(row.paymentAmount)}</strong>
                      </TableCell>
                    )}
                    <TableCell>{days(row.daysWaiting)}</TableCell>
                    <TableCell>
                      <FollowUpStatus row={row} onOpen={() => setSelected(row)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tenant</TableCell>
                  <TableCell>Property</TableCell>
                  <TableCell>Owner</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  {tab === "paid_online" && <TableCell>Method</TableCell>}
                  <TableCell>{tab === "awaiting_payment" ? "Status" : "OTS fee"}</TableCell>
                  <TableCell>Waiting</TableCell>
                  {tab === "paid_cash" && <TableCell />}
                  <TableCell>Follow-up</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.requestId} hover sx={{ ...rowSx(row), cursor: "pointer" }} onClick={() => setSelected(row)}>
                    <TableCell>{contactCell(row.tenantName, row.tenantEmail, row.tenantPhone)}</TableCell>
                    <TableCell>
                      {row.flatName}
                      {row.flatCity ? `, ${row.flatCity}` : ""}
                    </TableCell>
                    <TableCell>{row.landlordName || "—"}</TableCell>
                    <TableCell align="right">
                      <strong>{money(row.paymentAmount)}</strong>
                    </TableCell>
                    {tab === "paid_online" && (
                      <TableCell>{METHOD_LABEL[row.paymentMethod] || row.paymentMethod}</TableCell>
                    )}
                    <TableCell>
                      {tab === "awaiting_payment" ? (
                        <Chip
                          size="small"
                          label={row.paymentStatus}
                          sx={{
                            bgcolor: PAYMENT_STATUS_CHIP[row.paymentStatus]?.bg,
                            color: PAYMENT_STATUS_CHIP[row.paymentStatus]?.color,
                            textTransform: "capitalize",
                            fontWeight: 600,
                          }}
                        />
                      ) : row.otsFee?.required ? (
                        <Chip
                          size="small"
                          label={
                            row.otsFee.status === "pending" && !row.otsFee.submittedAt
                              ? "Not submitted"
                              : row.otsFee.status
                          }
                          sx={{
                            bgcolor: row.otsFee.status === "verified" ? "#dcfce7" : "#fee2e2",
                            color: row.otsFee.status === "verified" ? "#166534" : "#991b1b",
                            textTransform: "capitalize",
                            fontWeight: 600,
                          }}
                        />
                      ) : (
                        <Chip size="small" label="Not required" sx={{ bgcolor: "#e5e7eb" }} />
                      )}
                    </TableCell>
                    <TableCell>{days(row.daysWaiting)}</TableCell>
                    {tab === "paid_cash" && (
                      <TableCell align="right">
                        <Button
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate("/admin/ots-fee-receipts");
                          }}
                        >
                          Review fee
                        </Button>
                      </TableCell>
                    )}
                    <TableCell>
                      <FollowUpStatus row={row} onOpen={() => setSelected(row)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Box>

      <FollowUpDialog
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        title={selected?.tenantName || "Follow-up"}
        subtitle={activeTab?.label}
        details={selected ? detailsFor(selected, tab) : []}
        contacts={selected ? contactsFor(selected) : []}
        followUp={selected ? apply(selected).followUp : null}
        onSaved={record}
      />
    </>
  );
};

export default TenantFunnel;
